import { admittedSentimentLeadUrls, retainedSentimentSourceIds } from '../../../../../lib/reportGeneration/reportEvidenceSourceAdmission';
/**
 * Chapter 08 blocks (SPEC §3.08, §4): the source coverage matrix (always
 * shown; the only visual in a coverage-only report), the municipal archive
 * read-through, unscored records by reason, and the supporting tables (context
 * facts, source leads, discovery exclusions). Local stance vs context lives in
 * StanceBlocks.
 */
import React from 'react';
import type { ReportTheme } from '../../../../../types/reportTheme';
import type { SiteRagReport } from '../../../../../types/siteRag';
import { roleColor } from '../../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../../lib/reportSectionHeading';
import { EditorialKpiRail } from '../../../report/editorial';
import { RefLink } from '../../../report/RefLink';
import type { PsrArchiveAudit, PsrSourceCoverageState, PsrTier1Fact } from '../../../../../lib/sentiment/publicSentimentMetaV2Contract';
import {
  psrArchiveCaveat,
  psrCoverageTally,
  psrCoverageTallyText,
  psrExclusionLabel,
  psrFactCount,
  psrFormatDateTime,
  psrFormatDay,
  psrFunnel,
  psrPercent,
  psrReasonLabel,
  psrReferenceIndex,
  psrSourceCounts,
  psrSourceLabel,
  type PsrFunnelModel,
  type PsrReportView,
} from '../../../../../lib/sentiment/psrReportView';
import {
  CoverageLegend,
  CoverageMark,
  PSR_APPARATUS,
  PSR_CHART_WIDTH,
  PSR_SERIF,
  PsrBlock,
  PsrLegend,
  PsrMutedText,
  PsrNote,
  PsrSwatch,
  PsrTable,
  type PsrTableGroup,
  psrPalette,
} from './psrUi';
import { LocalVsContextBars, barPath } from './StanceBlocks';

/* ── References ────────────────────────────────────────────────────────── */

/**
 * A reference marker resolved by EXACT URL against the report's references;
 * an unresolved URL is printed as the URL itself, never the nearest ref.
 */
export function PsrRef({ theme, references, url }: { theme: ReportTheme; references: SiteRagReport['references']; url?: string }): React.ReactElement | null {
  if (!url) return null;
  const n = psrReferenceIndex(references, url);
  if (n !== undefined && references?.[n - 1]?.qualification === 'unavailable') return null;
  if (n !== undefined) return <span data-ref-resolved={n}><RefLink n={n} reference={references?.[n - 1]} /></span>;
  return (
    <a data-ref-unresolved href={url} target="_blank" rel="noreferrer" style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.caption, color: roleColor(theme, 'muted'), overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
      {url}
    </a>
  );
}

/* ── Source coverage matrix ────────────────────────────────────────────── */

function errorCell(theme: ReportTheme, row: PsrSourceCoverageState): React.ReactNode {
  const lines: React.ReactNode[] = [];
  if (row.failure) {
    lines.push(<span key="f">{row.failure}</span>);
    lines.push(<PsrMutedText key="fn" theme={theme}>Recorded with the scan; no coverage row</PsrMutedText>);
  } else if (row.errors.length) {
    lines.push(<PsrMutedText key="p" theme={theme}>Partial read; remaining records are unresolved. Diagnostics are retained in the saved collection receipt.</PsrMutedText>);
  } else if (row.coverage === 'unreported') {
    lines.push(<PsrMutedText key="u" theme={theme}>No coverage row, no recorded failure</PsrMutedText>);
  } else if (row.coverage === 'none' || row.coverage === 'partial') {
    lines.push(<PsrMutedText key="n" theme={theme}>No collection error recorded</PsrMutedText>);
  } else {
    lines.push(<span key="d" style={{ color: roleColor(theme, 'faint') }}>–</span>);
  }
  if (row.rows.length > 1) {
    lines.push(<PsrMutedText key="rows" theme={theme}>{row.rows.length} coverage rows: {row.rows.map(r => r.coverage).join(', ')}; the weakest is shown</PsrMutedText>);
  }
  return <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{lines}</span>;
}

export function SourceCoverageMatrix({ theme, view, entryCount, retainedSources }: { theme: ReportTheme; view: PsrReportView; entryCount?: number; retainedSources?: ReadonlySet<string> }): React.ReactElement | null {
  if (!view.scan || !view.coverage.length) return null;
  const coverageOnly = view.state === 'coverage-only';
  const assessed = view.index !== undefined;
  const faint = roleColor(theme, 'faint');
  const facts = (n: number) => (
    <span style={{ fontFamily: PSR_APPARATUS }}><span style={{ fontWeight: 600 }}>{n}</span> <span style={{ fontSize: REPORT_TEXT_PX.annotation, color: roleColor(theme, 'muted') }}>{n === 1 ? 'fact' : 'facts'}</span></span>
  );
  const dash = <span style={{ color: faint }}>—</span>;
  const recordsKnownZero = coverageOnly && entryCount === 0;
  const row = (state: PsrSourceCoverageState) => {
    const counts = psrSourceCounts(view.v2, state.sourceId);
    const coverage = <CoverageMark theme={theme} state={state.coverage} />;
    if (state.role === 'stance') {
      const records = coverageOnly ? (recordsKnownZero ? 0 : dash) : counts.rows;
      return coverageOnly
        ? [psrSourceLabel(state.sourceId), coverage, records, errorCell(theme, state)]
        : [psrSourceLabel(state.sourceId), coverage, counts.rows, counts.siteRows, assessed ? counts.assessedLocal : 0, errorCell(theme, state)];
    }
    const n = psrFactCount(view.v2, state.sourceId);
    return coverageOnly
      ? [psrSourceLabel(state.sourceId), coverage, facts(n), errorCell(theme, state)]
      : [psrSourceLabel(state.sourceId), coverage, facts(n), dash, dash, errorCell(theme, state)];
  };
  const groups: PsrTableGroup[] = [];
  const readCoverage = view.coverage.filter(row => (retainedSources ?? retainedSentimentSourceIds({ publicSentimentMeta: view.v2 })).has(row.sourceId) && (row.coverage === 'full' || row.coverage === 'partial'));
  const stance = readCoverage.filter(s => s.role === 'stance');
  const context = readCoverage.filter(s => s.role === 'context');
  const requested = readCoverage.filter(s => s.role === 'requested');
  if (stance.length) groups.push({ key: 'stance', label: 'Stance sources', detail: psrCoverageTallyText(psrCoverageTally(view.coverage, 'stance')), rows: stance.map(s => ({ key: s.sourceId, cells: row(s) })) });
  if (context.length) groups.push({ key: 'context', label: 'Context sources', detail: `${psrCoverageTallyText(psrCoverageTally(view.coverage, 'context'))} · never a stance`, rows: context.map(s => ({ key: s.sourceId, cells: row(s) })) });
  if (requested.length) groups.push({ key: 'requested', label: 'Other coverage rows', detail: 'recorded, not attempted this scan', rows: requested.map(s => ({ key: s.sourceId, cells: row({ ...s, role: 'context' }) })) });
  const stanceTally = psrCoverageTally(view.coverage, 'stance');
  const columns = coverageOnly
    ? [{ header: 'Source', width: 146 }, { header: 'Coverage', width: 116 }, { header: 'Records', align: 'right' as const, numeric: true, width: 80 }, { header: 'Errors' }]
    : [
      { header: 'Source', width: 146 }, { header: 'Coverage', width: 116 },
      { header: 'Records', align: 'right' as const, numeric: true, width: 80 }, { header: 'Local', align: 'right' as const, numeric: true, width: 60 },
      { header: 'Assessed local', align: 'right' as const, numeric: true, width: 84 }, { header: 'Errors' },
    ];
  return (
    <PsrBlock
      theme={theme}
      testId="psr-coverage-matrix"
      title="Source coverage"
      subtitle={coverageOnly
        ? 'Actually read sources only · unavailable attempts remain in the saved collection receipt'
        : 'Stance sources first · records (all scopes), local (site scope) and assessed local counts from the evidence inventory'}
      partial={view.partial && !coverageOnly}
      caveatPosition="top"
      caveat={`${stanceTally.full === 0 ? 'No stance source reported Full.' : `${stanceTally.full} of ${stanceTally.total} stance sources reported Full.`} Counts describe what arrived, not what exists.`}
      exhibit={false}
    >
      <CoverageLegend theme={theme} />
      <PsrTable theme={theme} testId="psr-coverage-matrix-table" columns={columns} groups={groups} />
      {coverageOnly && (
        <PsrNote theme={theme} italic>
          {entryCount === 0
            ? 'No archive funnel, reasons chart or context table is drawn: there are no records to chart.'
            : 'A coverage-only report draws no archive funnel, reasons chart or context table.'}
        </PsrNote>
      )}
    </PsrBlock>
  );
}

/* ── Municipal archive read-through ────────────────────────────────────── */

export function ArchiveFunnelTable({ theme, model, caption }: { theme: ReportTheme; model: PsrFunnelModel; caption?: React.ReactNode }): React.ReactElement | null {
  const a = model.audit;
  const rows: Array<{ key: string; cells: React.ReactNode[] }> = [];
  const push = (key: string, label: string, value: number | undefined, rate?: string) => {
    if (value === undefined) return;
    rows.push({ key, cells: [label, value, rate ?? '–'] });
  };
  push('listed', 'Documents listed', a.listedDocuments);
  push('window', 'In the observation window', a.inWindowDocuments, a.inWindowDocuments !== undefined && a.listedDocuments ? `${psrPercent(a.inWindowDocuments, a.listedDocuments)} of listed` : undefined);
  if (model.consistent) {
    push('read-window', 'Read, in window', a.readInWindowDocuments, a.readInWindowDocuments !== undefined && a.inWindowDocuments ? `${psrPercent(a.readInWindowDocuments, a.inWindowDocuments)} of in-window` : undefined);
  }
  push('read', 'Reads, attachments included', a.readDocuments);
  push('relevant', 'Relevant (mention a selected technology)', a.relevantDocuments, a.relevantDocuments !== undefined && a.readDocuments ? `${psrPercent(a.relevantDocuments, a.readDocuments)} of all reads` : undefined);
  push('att-listed', 'Attachments listed (not windowed)', a.listedAttachments);
  push('att-read', 'Attachments read', a.readAttachments, a.readAttachments !== undefined && a.listedAttachments ? `${psrPercent(a.readAttachments, a.listedAttachments)} of listed` : undefined);
  push('pages', 'Pages read (PDF pages visited)', a.readPages);
  push('ocr', 'of which recovered by OCR', a.ocrPages, a.ocrPages !== undefined && a.readPages ? `${psrPercent(a.ocrPages, a.readPages)} of pages read` : undefined);
  if (!rows.length) return null;
  return (
    <PsrTable
      theme={theme}
      testId="psr-archive-funnel-table"
      caption={caption}
      columns={[{ header: 'Archive stage', width: '52%' }, { header: 'Count', align: 'right', numeric: true }, { header: 'Step rate' }]}
      groups={[{ key: 'stages', rows }]}
    />
  );
}

function FunnelSvg({ theme, model }: { theme: ReportTheme; model: PsrFunnelModel }): React.ReactElement {
  const p = psrPalette(theme);
  const a = model.audit;
  const ink = roleColor(theme, 'ink');
  const muted = roleColor(theme, 'muted');
  const faint = roleColor(theme, 'faint');
  const X0 = 176;
  const MAXW = 330;
  const scaleMax = Math.max(1, a.listedDocuments ?? 0, a.listedAttachments ?? 0);
  const w = (n: number) => (n / scaleMax) * MAXW;
  const label = { fontFamily: PSR_SERIF, fontSize: 13, fontWeight: 600 } as const;
  const group = { fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 600, letterSpacing: 1.1 } as const;
  const value = { fontFamily: PSR_APPARATUS, fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' } as const;
  const note = { fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 400 } as const;
  type Row = { key: string; name: string; value: number; fill: string; onFill: string; remainderTo?: number; remainderText?: string; tail?: string };
  const docs: Row[] = [
    { key: 'listed', name: 'Listed in the archive', value: a.listedDocuments!, fill: p.amount[0], onFill: p.onAmount[0] },
    { key: 'window', name: 'In the observation window', value: a.inWindowDocuments!, fill: p.amount[1], onFill: p.onAmount[1], tail: `${psrPercent(a.inWindowDocuments!, a.listedDocuments!) ?? '–'} of listed` },
    {
      key: 'read', name: 'Read, in window', value: a.readInWindowDocuments!, fill: p.amount[2], onFill: p.onAmount[2],
      remainderTo: a.inWindowDocuments!, remainderText: `${model.unreadInWindow ?? 0} in window, not read`,
      tail: `${psrPercent(a.readInWindowDocuments!, a.inWindowDocuments!) ?? '–'} of in-window`,
    },
    { key: 'relevant', name: 'Relevant', value: a.relevantDocuments!, fill: p.amount[3], onFill: p.onAmount[3], tail: `${psrPercent(a.relevantDocuments!, a.readDocuments!) ?? '–'} of all ${a.readDocuments} reads · mention a selected technology` },
  ];
  const attachments: Row[] = a.listedAttachments !== undefined ? [
    { key: 'att-listed', name: 'Listed', value: a.listedAttachments, fill: p.amount[0], onFill: p.onAmount[0] },
    ...(a.readAttachments !== undefined ? [{
      key: 'att-read', name: 'Read', value: a.readAttachments, fill: p.amount[2], onFill: p.onAmount[2],
      remainderTo: a.listedAttachments, remainderText: `${model.unreadAttachments ?? 0} not read`, tail: `${psrPercent(a.readAttachments, a.listedAttachments) ?? '–'} of listed`,
    }] : []),
  ] : [];
  const drawRow = (row: Row, top: number) => {
    const y = top;
    const bw = w(row.value);
    const hasRemainder = row.remainderTo !== undefined && row.remainderTo > row.value;
    const rw = hasRemainder ? w(row.remainderTo!) : bw;
    const end = X0 + rw;
    const inside = bw >= 24;
    return (
      <g key={row.key} data-funnel-stage={row.key} data-value={row.value}>
        <text x="0" y={y + 12} fill={ink} style={label}>{row.name}</text>
        {bw > 0 && <path d={barPath(X0, X0 + bw, y, 16, false, !hasRemainder)} fill={row.fill}><title>{`${row.name}: ${row.value}`}</title></path>}
        {hasRemainder && <path data-funnel-remainder={row.remainderTo! - row.value} d={barPath(X0 + bw + (bw > 0 ? 2 : 0), end, y, 16, false, true)} fill={p.empty} />}
        {hasRemainder ? (
          <>
            {inside && <text x={X0 + bw / 2} y={y + 12} textAnchor="middle" fill={row.onFill} style={{ ...value, fontSize: 11.5 }}>{row.value}</text>}
            <text x={end + 8} y={y + 12.5} fill={roleColor(theme, 'body')} style={{ ...note, fontWeight: 600 }}>
              {!inside && <tspan style={value}>{row.value} </tspan>}
              {!inside && <tspan fill={muted} style={note}>read · </tspan>}
              {row.remainderText}
              {row.tail && <tspan dx="8" fill={muted} style={note}>· {row.tail}</tspan>}
            </text>
          </>
        ) : (
          <text x={end + 8} y={y + 12.5} fill={ink} style={value}>
            {row.value}
            {row.tail && <tspan dx="7" fill={muted} style={note}>{row.tail}</tspan>}
          </text>
        )}
      </g>
    );
  };
  const docsTop = 29;
  const pitch = 34;
  const dividerY = docsTop + docs.length * pitch + 1.5;
  const attTop = dividerY + 36;
  const height = attachments.length ? attTop + attachments.length * pitch - 6 : dividerY - 8;
  return (
    <svg data-testid="psr-archive-funnel-svg" viewBox={`0 0 ${PSR_CHART_WIDTH} ${height}`} width="100%" role="img" aria-label="Municipal archive read-through funnel" style={{ display: 'block', maxWidth: '100%' }}>
      <text x="0" y="12" fill={muted} style={group}>DOCUMENTS</text>
      {docs.map((row, i) => drawRow(row, docsTop + i * pitch))}
      {attachments.length > 0 && (
        <>
          <line x1="0" y1={dividerY} x2={PSR_CHART_WIDTH} y2={dividerY} stroke={roleColor(theme, 'hair')} strokeWidth="1" />
          <text x="0" y={dividerY + 19.5} fill={muted} style={group}>ATTACHMENTS</text>
          <text x={X0} y={dividerY + 19.5} fill={faint} style={note}>not windowed: listed across all dates</text>
          {attachments.map((row, i) => drawRow(row, attTop + i * pitch))}
        </>
      )}
    </svg>
  );
}

export function ArchiveFunnel({ theme, view, audit, index = 0 }: { theme: ReportTheme; view: PsrReportView; audit: PsrArchiveAudit; index?: number }): React.ReactElement {
  const model = psrFunnel(audit);
  const p = psrPalette(theme);
  const drawn = model.consistent && audit.listedDocuments !== undefined && audit.relevantDocuments !== undefined && audit.readDocuments !== undefined;
  const readAt = psrFormatDateTime(audit.fetchedAt);
  const extraErrors = audit.errorCount - audit.errors.length;
  return (
    <PsrBlock
      theme={theme}
      testId={index === 0 ? 'psr-archive-funnel' : `psr-archive-funnel-${index}`}
      title="Municipal archive read-through"
      subtitle={`${audit.jurisdiction}${readAt ? ` · read ${readAt}` : ''}`}
      partial={view.partial}
      caveat={psrArchiveCaveat(model)}
      table={drawn ? <ArchiveFunnelTable theme={theme} model={model} /> : undefined}
    >
      {drawn ? (
        <>
          <PsrLegend
            theme={theme}
            items={[
              { key: 'listed', swatch: <PsrSwatch color={p.amount[0]} width={16} />, label: 'Listed' },
              { key: 'window', swatch: <PsrSwatch color={p.amount[1]} width={16} />, label: 'In window' },
              { key: 'read', swatch: <PsrSwatch color={p.amount[2]} width={16} />, label: 'Read' },
              { key: 'relevant', swatch: <PsrSwatch color={p.amount[3]} width={16} />, label: 'Relevant' },
              { key: 'unread', swatch: <PsrSwatch color={p.empty} width={16} />, label: 'Not read (light remainder, labeled)' },
            ]}
          />
          <FunnelSvg theme={theme} model={model} />
        </>
      ) : (
        <>
          <ArchiveFunnelTable theme={theme} model={model} />
          <PsrNote theme={theme} testId="psr-archive-funnel-fallback">
            These counts do not nest (reads include linked attachments, which are not windowed), so no funnel is drawn and no unread remainder is computed.
          </PsrNote>
        </>
      )}
      <EditorialKpiRail
        theme={theme}
        columns={audit.readPages !== undefined ? 2 : 1}
        items={[
          ...(audit.readPages !== undefined ? [{ key: 'Pages read', value: String(audit.readPages), note: audit.ocrPages ? `PDF pages visited, ${audit.ocrPages} of them recovered by OCR` : 'PDF pages visited, none recovered by OCR' }] : []),
          { key: 'Collection errors', value: String(audit.errorCount), note: audit.errorCount ? 'listed below' : 'none recorded' },
        ]}
      />
      <PsrNote theme={theme}>
        {audit.errors.length > 0 && <>Errors, verbatim: {audit.errors.join('; ')}{extraErrors > 0 ? `; and ${extraErrors} more` : ''}. </>}
        Read pages count PDF pages only; HTML documents add no pages.
      </PsrNote>
    </PsrBlock>
  );
}

/* ── Unscored by reason ────────────────────────────────────────────────── */

export function UnscoredReasonsTable({ theme, view, caption }: { theme: ReportTheme; view: PsrReportView; caption?: React.ReactNode }): React.ReactElement | null {
  const rows = view.v2.unscoredByReason ?? [];
  if (!rows.length) return null;
  const local = rows.reduce((n, r) => n + r.local, 0);
  const context = rows.reduce((n, r) => n + r.context, 0);
  return (
    <PsrTable
      theme={theme}
      testId="psr-unscored-reasons-table"
      caption={caption}
      columns={[{ header: 'Reason (code)', width: '52%' }, { header: 'Local', align: 'right', numeric: true }, { header: 'Context', align: 'right', numeric: true }, { header: 'Total', align: 'right', numeric: true }]}
      groups={[{
        key: 'reasons',
        rows: [
          ...rows.map(r => ({ key: r.reasonCode, cells: [`${psrReasonLabel(r.reasonCode)} (${r.reasonCode})`, r.local, r.context, r.local + r.context] })),
          { key: 'total', cells: ['Total', local, context, local + context] },
        ],
      }]}
    />
  );
}

function reasonTicks(max: number): { max: number; ticks: number[] } {
  if (!(max > 0)) return { max: 1, ticks: [] };
  const steps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000];
  const step = steps.find(s => Math.ceil(max / s) <= 5) ?? Math.ceil(max / 5);
  const n = Math.ceil(max / step);
  return { max: n * step, ticks: Array.from({ length: n }, (_, i) => (i + 1) * step) };
}

export function UnscoredReasonsChart({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  const rows = view.v2.unscoredByReason ?? [];
  if (view.state === 'coverage-only' || !rows.length) return null;
  const p = psrPalette(theme);
  const ink = roleColor(theme, 'ink');
  const muted = roleColor(theme, 'muted');
  const rule = roleColor(theme, 'rule');
  const local = rows.reduce((n, r) => n + r.local, 0);
  const context = rows.reduce((n, r) => n + r.context, 0);
  const X0 = 232;
  const axis = reasonTicks(Math.max(...rows.map(r => r.local + r.context)));
  const unit = 340 / axis.max;
  const pitch = 30;
  const bottom = 4 + rows.length * pitch;
  const height = bottom + 21;
  const audit = view.v2.municipalArchiveAudit?.[0];
  const model = audit ? psrFunnel(audit) : undefined;
  const unread = model && model.consistent ? [
    model.unreadInWindow ? `${model.unreadInWindow} unread ${model.unreadInWindow === 1 ? 'document' : 'documents'}` : '',
    model.unreadAttachments ? `${model.unreadAttachments} unread ${model.unreadAttachments === 1 ? 'attachment' : 'attachments'}` : '',
  ].filter(Boolean) : [];
  const caveat = unread.length
    ? `The ${unread.join(' and ')} have no reason code and are not counted here.`
    : 'Records that were not read have no reason code and are not counted here.';
  const total = local + context;
  const allLocalUnscored = view.index === undefined && (view.site?.total ?? 0) === local && local > 0;
  return (
    <PsrBlock
      theme={theme}
      testId="psr-unscored-reasons"
      title="Why records were not scored"
      subtitle={`Unscored records by reason · ${local} local, ${context} context`}
      partial={view.partial}
      caveat={caveat}
      table={<UnscoredReasonsTable theme={theme} view={view} />}
    >
      <PsrLegend
        theme={theme}
        items={[
          { key: 'local', swatch: <PsrSwatch color={p.amount[3]} />, label: `Local (site scope) · ${local}` },
          { key: 'context', swatch: <PsrSwatch color={p.amount[1]} />, label: `Context (state + national) · ${context}` },
          { key: 'end', swatch: null, label: <span style={{ color: muted }}>Bar end = total</span> },
        ]}
      />
      <svg data-testid="psr-unscored-reasons-svg" viewBox={`0 0 ${PSR_CHART_WIDTH} ${height}`} width="100%" role="img" aria-label="Unscored records by reason" style={{ display: 'block', maxWidth: '100%' }}>
        {axis.ticks.map(t => (
          <g key={t}>
            <line x1={X0 + t * unit} y1="4" x2={X0 + t * unit} y2={bottom} stroke={rule} strokeWidth="1" strokeDasharray="2 4" />
            <text x={X0 + t * unit} y={bottom + 15} textAnchor="middle" fill={muted} style={{ fontFamily: PSR_APPARATUS, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{t}</text>
          </g>
        ))}
        <line x1={X0 - 0.5} y1="4" x2={X0 - 0.5} y2={bottom} stroke={muted} strokeWidth="1" />
        <text x={X0} y={bottom + 15} textAnchor="middle" fill={muted} style={{ fontFamily: PSR_APPARATUS, fontSize: 12 }}>0</text>
        {rows.map((r, i) => {
          const y = 10 + i * pitch;
          const lw = r.local * unit;
          const cw = r.context * unit;
          const lEnd = X0 + lw;
          const cStart = r.local > 0 ? lEnd + 2 : X0;
          const cEnd = cStart + cw - (r.local > 0 ? 2 : 0);
          const end = r.context > 0 ? cEnd : lEnd;
          return (
            <g key={r.reasonCode} data-reason={r.reasonCode} data-local={r.local} data-context={r.context}>
              <text x="1" y={y + 12} fill={ink} style={{ fontFamily: PSR_SERIF, fontSize: 12.64 }}>{psrReasonLabel(r.reasonCode)}</text>
              {r.local > 0 && (
                <path d={barPath(X0, lEnd, y, 16, false, r.context === 0)} fill={p.amount[3]}><title>{`${psrReasonLabel(r.reasonCode)}: ${r.local} local`}</title></path>
              )}
              {r.local > 0 && lw >= 18 && <text x={X0 + lw / 2} y={y + 12} textAnchor="middle" fill={p.onAmount[3]} style={{ fontFamily: PSR_APPARATUS, fontSize: 11.5, fontWeight: 600 }}>{r.local}</text>}
              {r.context > 0 && (
                <path d={barPath(cStart, cEnd, y, 16, false, true)} fill={p.amount[1]}><title>{`${psrReasonLabel(r.reasonCode)}: ${r.context} context`}</title></path>
              )}
              {r.context > 0 && cEnd - cStart >= 18 && <text x={(cStart + cEnd) / 2} y={y + 12} textAnchor="middle" fill={p.onAmount[1]} style={{ fontFamily: PSR_APPARATUS, fontSize: 11.5, fontWeight: 600 }}>{r.context}</text>}
              <text x={end + 8} y={y + 12} fill={ink} style={{ fontFamily: PSR_APPARATUS, fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.local + r.context}</text>
            </g>
          );
        })}
      </svg>
      <PsrNote theme={theme}>
        {allLocalUnscored ? `All ${local} local records are unscored. ` : `${total} records were set aside. `}
        Unscored is not neutral: each record stays in the evidence inventory with its reason, outside the index. Sorted by total.
      </PsrNote>
    </PsrBlock>
  );
}

/* ── Supporting tables ─────────────────────────────────────────────────── */

export function formatPsrFactValue(fact: Pick<PsrTier1Fact, 'value' | 'units'>): string {
  const { value, units } = fact;
  if (typeof value !== 'number') return units ? `${value} ${units}` : String(value);
  const n = value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (units === 'USD') return `$${n}`;
  if (units === '%' || units === 'percent') return `${n}%`;
  return units ? `${n} ${units}` : n;
}

export function ContextFactsTable({ theme, view, references }: { theme: ReportTheme; view: PsrReportView; references: SiteRagReport['references'] }): React.ReactElement | null {
  const facts = view.v2.tier1Facts ?? [];
  if (view.state === 'coverage-only' || !facts.length) return null;
  return (
    <PsrBlock theme={theme} testId="psr-context-facts" title="Context facts · never a stance" exhibit={false}>
      <PsrTable
        theme={theme}
        testId="psr-context-facts-table"
        columns={[{ header: 'Fact', width: '38%' }, { header: 'Value', align: 'right', numeric: true }, { header: 'Source' }, { header: 'As of' }, { header: 'Ref', align: 'right', width: 64 }]}
        groups={[{ key: 'facts', rows: facts.map((fact, i) => ({ key: `${fact.sourceId}-${fact.key ?? i}`, cells: [fact.label, formatPsrFactValue(fact), psrSourceLabel(fact.sourceId), fact.observedAt, <PsrRef key="r" theme={theme} references={references} url={fact.url} />] })) }]}
      />
      {(view.v2.tier1FactsOmitted ?? 0) > 0 && <PsrNote theme={theme}>{view.v2.tier1FactsOmitted} further context facts are not shown.</PsrNote>}
    </PsrBlock>
  );
}

const humanize = (value: string) => (value ? value[0]!.toUpperCase() + value.slice(1).replace(/-/g, ' ') : value);

export function SourceLeadsTable({ theme, leads, references, admittedUrls }: { theme: ReportTheme; leads: unknown; references: SiteRagReport['references']; admittedUrls?: ReadonlySet<string> }): React.ReactElement | null {
  const rows = Array.isArray(leads) ? leads.filter((lead): lead is { url: string; title: string; publicationDate?: string | null; classification?: string; limitations?: string[] } =>
    !!lead && typeof lead === 'object' && typeof (lead as { title?: unknown }).title === 'string' && typeof (lead as { url?: unknown }).url === 'string' && admittedUrls?.has((lead as { url: string }).url) === true) : [];
  if (!rows.length) return null;
  return (
    <PsrBlock theme={theme} testId="psr-source-leads" title="Read source context · unverified, not scored" exhibit={false}>
      <PsrTable
        theme={theme}
        testId="psr-source-leads-table"
        columns={[{ header: 'Lead', width: '36%' }, { header: 'Published' }, { header: 'Kind' }, { header: 'Limitations', width: '26%' }, { header: 'Ref', align: 'right', width: 64 }]}
        groups={[{
          key: 'leads',
          rows: rows.map((lead, i) => ({
            key: `${lead.url}-${i}`,
            cells: [
              lead.title,
              (typeof lead.publicationDate === 'string' && psrFormatDay(lead.publicationDate)) || 'unknown',
              typeof lead.classification === 'string' ? humanize(lead.classification) : '—',
              Array.isArray(lead.limitations) ? lead.limitations.filter(l => typeof l === 'string').join(' ') : '',
              <PsrRef key="r" theme={theme} references={references} url={lead.url} />,
            ],
          })),
        }]}
      />
    </PsrBlock>
  );
}

export function DiscoveryExclusionsTable({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  const counts = view.v2.discoveryExclusionCounts;
  if (view.state === 'coverage-only' || !counts || counts.total === 0) return null;
  const listed = counts.byReason.reduce((n, r) => n + r.count, 0);
  return (
    <PsrBlock theme={theme} testId="psr-discovery-exclusions" title="Discovery exclusions" exhibit={false}>
      <PsrTable
        theme={theme}
        testId="psr-discovery-exclusions-table"
        columns={[{ header: 'Excluded before scoping' }, { header: 'Records', align: 'right', numeric: true, width: 96 }]}
        groups={[{
          key: 'ex',
          rows: [
            ...counts.byReason.map(r => ({ key: r.reason, cells: [psrExclusionLabel(r.reason), r.count] })),
            ...(listed < counts.total ? [{ key: 'other', cells: ['Other reasons', counts.total - listed] }] : []),
            { key: 'total', cells: ['Total', counts.total] },
          ],
        }]}
      />
      <PsrNote theme={theme}>Dropped at discovery, before scoping: these never enter the ledger, the counts or the index.</PsrNote>
    </PsrBlock>
  );
}

/** Chapter 08 in design order. Coverage-only: the matrix alone. */
export function SentimentResearchBlocks({ theme, view, report }: { theme: ReportTheme; view: PsrReportView; report: SiteRagReport }): React.ReactElement {
  const entryCount = typeof report.publicSentimentMeta?.entryCount === 'number' ? report.publicSentimentMeta.entryCount : undefined;
  const assessed = view.index !== undefined;
  const leads = (report.publicSentimentMeta as { sourceLeads?: unknown } | undefined)?.sourceLeads;
  const audits = view.state === 'coverage-only' ? [] : (view.v2.municipalArchiveAudit ?? []).filter(row => (row.readDocuments ?? 0) > 0 || (row.readAttachments ?? 0) > 0);
  return (
    <div data-testid="psr-research-blocks" data-psr-state={view.state} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {assessed && <LocalVsContextBars theme={theme} view={view} />}
      <SourceCoverageMatrix theme={theme} view={view} entryCount={entryCount} retainedSources={retainedSentimentSourceIds(report)} />
      {audits.map((audit, i) => <ArchiveFunnel key={`${audit.jurisdiction}-${i}`} theme={theme} view={view} audit={audit} index={i} />)}
      <UnscoredReasonsChart theme={theme} view={view} />
      {!assessed && <LocalVsContextBars theme={theme} view={view} />}
      <ContextFactsTable theme={theme} view={view} references={report.references} />
      {view.state !== 'coverage-only' && <SourceLeadsTable theme={theme} leads={leads} references={report.references} admittedUrls={admittedSentimentLeadUrls(report)} />}
      <DiscoveryExclusionsTable theme={theme} view={view} />
    </div>
  );
}

